/* Eight-second photo rotation with visitor controls and a matching progress ring. */
if (!customElements.get('haro-provider-gallery')) {
  customElements.define('haro-provider-gallery', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.dots = [...this.querySelectorAll('[data-gallery-index]')];
      if (this.slides.length < 2) return;
      this.status = this.querySelector('[data-gallery-status]');
      this.progress = this.querySelector('[data-gallery-progress]');
      this.toggle = this.querySelector('[data-gallery-toggle]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.motion.matches;
      this.focused = this.contains(document.activeElement);
      this.inView = false;
      this.elapsed = 0;
      this.querySelector('[data-gallery-controls]').hidden = false;
      this.show(0);
      this.updateToggle();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-gallery-toggle')) {
          this.paused = !this.paused;
          this.updateToggle();
          this.syncTimer();
        } else if (button.hasAttribute('data-gallery-next')) this.show(this.index + 1);
        else if (button.hasAttribute('data-gallery-prev')) this.show(this.index - 1);
        else if (button.hasAttribute('data-gallery-index')) this.show(Number(button.dataset.galleryIndex));
      }, options);
      this.addEventListener('keydown', event => {
        if (!event.target.closest('button') || event.altKey || event.ctrlKey || event.metaKey) return;
        this.focused = true;
        this.syncTimer();
        const actions = { ArrowRight: this.index + 1, ArrowLeft: this.index - 1, Home: 0, End: this.slides.length - 1 };
        if (!(event.key in actions)) return;
        event.preventDefault();
        this.show(actions[event.key]);
      }, options);
      this.addEventListener('focusin', event => {
        this.focused = event.target.matches(':focus-visible');
        this.syncTimer();
      }, options);
      this.addEventListener('focusout', event => {
        this.focused = this.contains(event.relatedTarget);
        this.syncTimer();
      }, options);
      document.addEventListener('visibilitychange', () => this.syncTimer(), options);
      this.motion.addEventListener('change', event => {
        this.paused = event.matches;
        this.updateToggle();
        this.syncTimer();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        this.syncTimer();
      }, { threshold: 0 });
      this.observer.observe(this);
    }

    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => { slide.hidden = i !== this.index; });
      this.dots.forEach((dot, i) => {
        if (i === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
      // Start loading the upcoming image before it becomes visible.
      const nextImage = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (nextImage) nextImage.loading = 'eager';
      this.elapsed = 0;
      this.progress.style.setProperty('--hp-gallery-progress', '0');
      this.syncTimer();
    }

    updateToggle() {
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.querySelector('[data-gallery-pause-icon]').toggleAttribute('hidden', this.paused);
      this.querySelector('[data-gallery-play-icon]').toggleAttribute('hidden', !this.paused);
    }

    syncTimer() {
      cancelAnimationFrame(this.frame);
      this.status.setAttribute('aria-live', this.paused || this.focused ? 'polite' : 'off');
      if (this.paused || this.focused || !this.inView || document.hidden) return;
      this.lastTime = performance.now();
      this.frame = requestAnimationFrame(now => this.tick(now));
    }

    tick(now) {
      this.elapsed += now - this.lastTime;
      this.lastTime = now;
      if (this.elapsed >= 8000) {
        this.show(this.index + 1);
        return;
      }
      this.progress.style.setProperty('--hp-gallery-progress', String(this.elapsed / 8000));
      this.frame = requestAnimationFrame(time => this.tick(time));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}
